import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, BellRing, MessageCircle } from "lucide-react";
import { api, coachHeaders, heureAff } from "../lib/api";
import { openWhatsApp, buildText, blocReponse } from "./ShareWhatsApp";
import { CatBadge } from "./CatBadge";
import { nomCat } from "../lib/categorie";

const key = (nom, prenom) => `${nom.trim().toLowerCase()}|${prenom.trim().toLowerCase()}`;
const STATUT = { present: ["Présent", "bg-lime"], absent: ["Absent", "bg-signal"] };

const AddPlayer = ({ onAdd }) => {
  const [f, setF] = useState({ nom: "", prenom: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.nom.trim() || !f.prenom.trim()) { toast.error("Nom et prénom obligatoires"); return; }
    if (await onAdd(f)) setF({ nom: "", prenom: "" });
  };
  const input = "min-w-0 flex-1 rounded-xl border border-line bg-ink px-3 py-2 text-chalk outline-none focus:border-lime";
  return (
    <form onSubmit={submit} data-testid="roster-add-form" className="mt-3 flex gap-2">
      <input data-testid="roster-add-nom-input" placeholder="Nom" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} className={input} />
      <input data-testid="roster-add-prenom-input" placeholder="Prénom" value={f.prenom} onChange={(e) => setF({ ...f, prenom: e.target.value })} className={input} />
      <button data-testid="roster-add-button" aria-label="Ajouter" className="shrink-0 rounded-xl bg-lime px-3 text-ink"><Plus className="h-5 w-5" /></button>
    </form>
  );
};

export const Roster = ({ code, match, reponses, onChange }) => {
  const [joueurs, setJoueurs] = useState([]);
  const cid = match?.id;
  const load = useCallback(() => {
    if (!cid) return;
    api.get("/joueurs", { ...coachHeaders(code), params: { convocation_id: cid } }).then((r) => setJoueurs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [code, cid]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChange(); };
  const add = async (f) => {
    try { await api.post("/joueurs", f, { ...coachHeaders(code), params: { convocation_id: cid } }); refresh(); return true; }
    catch (e) { toast.error(e?.response?.data?.detail || "Ajout impossible"); return false; }
  };
  const toggle = async (j) => {
    setJoueurs((list) => list.map((x) => (x.id === j.id ? { ...x, convoque: !x.convoque } : x)));
    await api.patch(`/joueurs/${j.id}`, { convoque: !j.convoque, convocation_id: cid }, coachHeaders(code));
    onChange();
  };
  const remove = async (j) => { await api.delete(`/joueurs/${j.id}`, coachHeaders(code)); refresh(); };
  const setStatut = async (j, statut) => {
    try {
      await api.put("/coach/reponses", { nom: j.nom, prenom: j.prenom, statut, convocation_id: cid }, coachHeaders(code));
      toast.success(`${j.prenom} : ${statut === "present" ? "Présent" : "Absent"}`);
      onChange();
    } catch { toast.error("Modification impossible"); }
  };

  const byKey = Object.fromEntries((reponses || []).map((r) => [key(r.nom, r.prenom), r.statut]));
  const nbConvoques = joueurs.filter((j) => j.convoque).length;
  const sansReponse = joueurs.filter((j) => j.convoque && !byKey[key(j.nom, j.prenom)]);
  const relancer = () => {
    const m = match || {};
    openWhatsApp([
      `Rappel convocation ${m.club ?? ""} - ${m.adversaire ?? ""}`,
      `Date : ${m.date ?? ""} - Rendez-vous : ${heureAff(m.rendez_vous)}`,
      `Pas encore de réponse de : ${sansReponse.map((j) => nomCat(j)).join(", ")}`,
      "",
      ...blocReponse({ ...m, id: cid }),
    ].join("\n"));
  };

  return (
    <div data-testid="roster-section" className="rounded-2xl border border-line bg-pitch p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl uppercase text-chalk">Joueurs convoqués</h2>
        <span data-testid="roster-convoques-count" className="font-mono text-xs uppercase text-dim">{nbConvoques} convoqué{nbConvoques > 1 ? "s" : ""} / {joueurs.length}</span>
      </div>
      <p className="mt-1 text-sm text-dim">Clique sur un enfant pour le convoquer ou non à ce match. Présent / Absent : changer toi-même son statut.</p>
      <button type="button" data-testid="roster-send-convocation-whatsapp-button" disabled={nbConvoques === 0}
        onClick={() => openWhatsApp(buildText({ ...(match || {}), id: cid }, joueurs.filter((j) => j.convoque)))}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 font-semibold text-ink transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-40">
        <MessageCircle className="h-4 w-4" /> Envoyer la convocation sur WhatsApp ({nbConvoques} convoqué{nbConvoques > 1 ? "s" : ""})
      </button>
      {sansReponse.length > 0 && (
        <button type="button" data-testid="roster-relance-whatsapp-button" onClick={relancer} className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#25D366] px-5 py-3 font-semibold text-[#25D366] transition-colors duration-300 hover:bg-[#25D366] hover:text-ink">
          <BellRing className="h-4 w-4" /> Relancer sur WhatsApp ({sansReponse.length} sans réponse)
        </button>
      )}
      <ul className="mt-3 divide-y divide-line">
        {joueurs.length === 0 && <li data-testid="roster-empty" className="py-4 text-sm text-dim">Ajoute d'abord les enfants dans l'onglet « Effectif ».</li>}
        {joueurs.map((j) => {
          const cur = byKey[key(j.nom, j.prenom)];
          return (
            <li key={j.id} data-testid="roster-row" className="flex items-center gap-2 py-1">
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg px-1 py-1.5 hover:bg-ink">
                <input type="checkbox" data-testid={`roster-convoque-${j.id}`} checked={j.convoque} onChange={() => toggle(j)} className="h-5 w-5 shrink-0 accent-[#D4AF37]" aria-label={`Convoquer ${j.prenom} ${j.nom}`} />
                <span className={`min-w-0 flex-1 truncate ${j.convoque ? "text-chalk" : "text-dim line-through"}`}>{j.prenom} {j.nom.toUpperCase()}<CatBadge j={j} testid={`roster-categorie-${j.id}`} /></span>
              </label>
              <div className="flex shrink-0 gap-1" role="group" aria-label="Statut">
                {Object.entries(STATUT).map(([k, [label, bg]]) => (
                  <button key={k} type="button" data-testid={`roster-set-${k}-${j.id}`} onClick={() => cur !== k && setStatut(j, k)} aria-pressed={cur === k}
                    className={`rounded-full px-2 py-0.5 font-mono text-[11px] uppercase transition-colors ${cur === k ? `${bg} text-ink` : "border border-line text-dim hover:border-chalk/50 hover:text-chalk"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
